import { useEffect, useRef, useState } from "react";

/**
 * Tech cursor glow — a circular halo around the cursor blending an
 * orange/blue gradient. Clicking emits a pulse ring of the same gradient.
 * Also exposes --cursor-x / --cursor-y CSS variables on <html> so the
 * liquid-glass layer can react to cursor position.
 */
export const CursorGlow = () => {
  const ref = useRef<HTMLDivElement>(null);
  const pulseRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const target = useRef({ x: 0, y: 0 });
  const current = useRef({ x: 0, y: 0 });
  const raf = useRef<number | null>(null);

  useEffect(() => {
    if (window.matchMedia("(pointer: coarse)").matches) return;

    const onMove = (e: MouseEvent) => {
      target.current.x = e.clientX;
      target.current.y = e.clientY;
      if (!visible) setVisible(true);
    };
    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    let lastPulse = 0;
    const onClick = () => {
      const el = pulseRef.current;
      if (!el) return;
      const now = performance.now();
      // Throttle: ignore clicks within 450ms of the previous pulse
      if (now - lastPulse < 450) return;
      lastPulse = now;
      el.classList.remove("cursor-pulse-anim");
      // Force reflow to restart animation
      void el.offsetWidth;
      el.classList.add("cursor-pulse-anim");
    };

    const tick = () => {
      current.current.x += (target.current.x - current.current.x) * 0.18;
      current.current.y += (target.current.y - current.current.y) * 0.18;
      const x = current.current.x;
      const y = current.current.y;
      if (ref.current) {
        ref.current.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%)`;
      }
      if (pulseRef.current) {
        pulseRef.current.style.left = `${x}px`;
        pulseRef.current.style.top = `${y}px`;
      }
      document.documentElement.style.setProperty("--cursor-x", `${x}px`);
      document.documentElement.style.setProperty("--cursor-y", `${y}px`);
      raf.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mousedown", onClick);
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    raf.current = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mousedown", onClick);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, [visible]);

  return (
    <>
      <style>{`
        @keyframes cursorPulse {
          0%   { opacity: 0.18; width: 30px;  height: 30px;  }
          60%  { opacity: 0.08; }
          100% { opacity: 0;    width: 140px; height: 140px; }
        }
        .cursor-pulse-anim { animation: cursorPulse 500ms ease-out forwards; }
      `}</style>

      {/* Glow halo */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 z-[1] overflow-hidden"
        style={{ mixBlendMode: "screen" }}
      >
        <div
          ref={ref}
          className="absolute top-0 left-0 will-change-transform transition-opacity duration-500 rounded-full"
          style={{
            width: "260px",
            height: "260px",
            opacity: visible ? 0.55 : 0,
            background:
              "radial-gradient(circle at 30% 50%, hsla(18, 100%, 55%, 0.55) 0%, transparent 55%), " +
              "radial-gradient(circle at 70% 50%, hsla(212, 100%, 58%, 0.55) 0%, transparent 55%)",
            filter: "blur(32px)",
          }}
        />

        {/* Click pulse ring (positioned via left/top, animated via width/height) */}
        <div
          ref={pulseRef}
          className="absolute rounded-full will-change-transform"
          style={{
            left: 0,
            top: 0,
            width: "0px",
            height: "0px",
            transform: "translate(-50%, -50%)",
            opacity: 0,
            background:
              "linear-gradient(90deg, hsla(18, 100%, 55%, 0.3) 0%, hsla(212, 100%, 60%, 0.3) 100%)",
            WebkitMaskImage:
              "radial-gradient(circle at 50% 50%, transparent 46%, black 56%, black 62%, transparent 74%)",
            maskImage:
              "radial-gradient(circle at 50% 50%, transparent 46%, black 56%, black 62%, transparent 74%)",
            filter: "blur(6px) saturate(170%)",
            mixBlendMode: "screen",
          }}
        />
      </div>
    </>
  );
};


export default CursorGlow;
